import React from 'react';
import { StepContainer } from '../StepContainer';
import { FormSection } from './form/formSection';
import { FormItem } from './form/formItem';

interface IsolationStepProps {
  data: any;
  onChange: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const IsolationStep: React.FC<IsolationStepProps> = ({
  data = {
    mursExterieurs: {},
    combles: {},
    planchersBas: {},
    cloisonsInterieures: {},
    etancheiteAir: {},
    etancheiteToiture: {},
  },
  onChange,
  onNext,
  onBack
}) => {

  // Fonction pour mettre à jour les sections de données
  const updateSection = (section: string, key: string, value: any) => {
    onChange({
      ...data,
      [section]: {
        ...data[section],
        [key]: value
      }
    });
  };

  return (
    <StepContainer
      title="Isolation et Étanchéité"
      description="Détaillez les travaux d'isolation et d'étanchéité du bâtiment"
      onNext={onNext}
      onBack={onBack}
    >
      <div className="space-y-6">

        {/* Section Isolation des murs extérieurs */}
        <FormSection
          title="1. Isolation des murs extérieurs"
          description="Isolation thermique par l'extérieur ou l'intérieur"
        >
          <div className="space-y-4">
            <FormItem
              label="ITE - Polystyrène expansé (R=3) avec enduit et peinture"
              value={data.mursExterieurs?.ite || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('mursExterieurs', 'ite', value)}
              withSurface={true}
            />
            <FormItem
              label="ITI - Laine de verre (R=3)"
              value={data.mursExterieurs?.iti || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('mursExterieurs', 'iti', value)}
              withSurface={true}
            />
          </div>
        </FormSection>

        {/* Section Isolation des combles */}
        <FormSection
          title="2. Isolation des combles"
          description="Isolation des combles perdus ou aménageables"
        >
          <div className="space-y-4">
            <FormItem
              label="Isolation au sol - Laine de verre/soufflée 30cm (R=7)"
              value={data.combles?.isolationSol || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('combles', 'isolationSol', value)}
              withSurface={true}
            />
            <FormItem
              label="Isolation sous toiture - Laine de verre 30cm (R=7)"
              value={data.combles?.isolationSousToiture || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('combles', 'isolationSousToiture', value)}
              withSurface={true}
            />
          </div>
        </FormSection>

        {/* Section Isolation des planchers bas */}
        <FormSection
          title="3. Isolation des planchers bas"
          description="Isolation des planchers en contact avec le sol"
        >
          <div className="space-y-4">
            <FormItem
              label="Plancher sur terre-plein - Polyuréthane 15cm (R=2)"
              value={data.planchersBas?.terrePlein || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('planchersBas', 'terrePlein', value)}
              withSurface={true}
            />
            <FormItem
              label="Plancher sur vide sanitaire - Laine soufflée 20cm (R=2)"
              value={data.planchersBas?.videSanitaire || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('planchersBas', 'videSanitaire', value)}
              withSurface={true}
            />
          </div>
        </FormSection>

        {/* Section Isolation des cloisons intérieures */}
        <FormSection
          title="4. Isolation des cloisons intérieures"
          description="Isolation thermique et acoustique des cloisons"
        >
          <div className="space-y-4">
            <FormItem
              label="Cloisons vers volume non chauffé (Panneaux placo + Laine de verre 12cm)"
              value={data.cloisonsInterieures?.volumeNonChauffe || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('cloisonsInterieures', 'volumeNonChauffe', value)}
              withSurface={true}
            />
            <FormItem
              label="Cloisons acoustiques (Panneaux placo acoustique + laine 10cm)"
              value={data.cloisonsInterieures?.acoustique || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('cloisonsInterieures', 'acoustique', value)}
              withSurface={true}
            />
          </div>
        </FormSection>

        {/* Section Étanchéité à l'air */}
        <FormSection
          title="5. Étanchéité à l'air"
          description="Traitement de l'étanchéité à l'air"
        >
          <div className="space-y-4">
            <FormItem
              label="Calfeutrage des fenêtres et portes"
              value={data.etancheiteAir?.calfeutrage || { selected: false }}
              onChange={(value) => updateSection('etancheiteAir', 'calfeutrage', value)}
            />
            <FormItem
              label="Traitement des fuites d'air dans les murs/plafonds"
              value={data.etancheiteAir?.traitementFuites || { selected: false }}
              onChange={(value) => updateSection('etancheiteAir', 'traitementFuites', value)}
            />
            <FormItem
              label="Test d'infiltrométrie après travaux"
              value={data.etancheiteAir?.testInfiltrometrie || { selected: false }}
              onChange={(value) => updateSection('etancheiteAir', 'testInfiltrometrie', value)}
            />
          </div>
        </FormSection>

        {/* Section Étanchéité de la toiture */}
        <FormSection
          title="6. Étanchéité de la toiture"
          description="Travaux d'étanchéité de toiture"
        >
          <div className="space-y-4">
            <FormItem
              label="Couverture en tuiles"
              value={data.etancheiteToiture?.tuiles || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('etancheiteToiture', 'tuiles', value)}
              withSurface={true}
            />
            <FormItem
              label="Couverture en ardoise"
              value={data.etancheiteToiture?.ardoise || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('etancheiteToiture', 'ardoise', value)}
              withSurface={true}
            />
            <FormItem
              label="Rénovation étanchéité toit-terrasse"
              value={data.etancheiteToiture?.toitTerrasse || { selected: false, surface: 0 }}
              onChange={(value) => updateSection('etancheiteToiture', 'toitTerrasse', value)}
              withSurface={true}
            />
          </div>
        </FormSection>

      </div>
    </StepContainer>
  );
};

export default IsolationStep;